import React, { useState, useRef, useEffect } from 'react';
import { Search, Loader, Building2 } from 'lucide-react';

export interface NzbnEntity {
  entityName?: string;
  nzbn?: string;
  entityType?: string;
  [key: string]: unknown;
}

export interface NzbnAutocompleteProps {
  onEntitySelect: (entity: NzbnEntity) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Show only when country is NZ */
  showOnlyForNz?: boolean;
  country?: string;
}

const DEBOUNCE_MS = 300;

export const NzbnAutocomplete: React.FC<NzbnAutocompleteProps> = ({
  onEntitySelect,
  placeholder = 'Search by company name or NZBN...',
  className = '',
  disabled = false,
  showOnlyForNz = false,
  country = '',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NzbnEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const isNz = country?.toUpperCase() === 'NZ' || country?.toLowerCase() === 'new zealand';
  const visible = !showOnlyForNz || isNz;

  useEffect(() => {
    if (!visible || query.length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const token = localStorage.getItem('token');
      fetch(`${apiUrl}/api/nzbn/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data?.entities) {
            setResults(data.data.entities);
            setError(null);
            setIsOpen(true);
          } else if (data.message) {
            setError(data.message);
            setResults([]);
          } else {
            setResults([]);
          }
        })
        .catch((err) => {
          setError(err.message || 'Search failed');
          setResults([]);
        })
        .finally(() => {
          setLoading(false);
        });
      setLoading(true);
      debounceRef.current = null;
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, apiUrl, visible]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (entity: NzbnEntity) => {
    setQuery('');
    onEntitySelect(entity);
    setIsOpen(false);
    setResults([]);
  };

  const getEntityLabel = (e: NzbnEntity) => {
    const name = e.entityName || (e as any).name || 'Unknown';
    const nzbn = e.nzbn || (e as any).nzbn;
    return nzbn ? `${name} (${nzbn})` : name;
  };

  if (!visible) return null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        NZBN Lookup (NZ businesses)
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-9 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
        />
        {loading && (
          <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{error}</p>
      )}
      {isOpen && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
          {results.map((entity, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => handleSelect(entity)}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{getEntityLabel(entity)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
